export type AnchorMode = 'centered' | 'default' | 'crs' | 'custom'
export type CenteringStatus = 'centered' | 'skipped_overflow'

// Structural shape of the Model fields these resolvers read. Defined inline to
// avoid a circular import with Model.ts (which imports AnchorMode/CenteringStatus
// from here). wgs84_position is included as a legacy fallback for models
// processed before the anchors-block rollout.
export type AnchorableModel = {
    anchor_mode?: AnchorMode | null
    custom_anchor?: [number, number, number, number, number] | null
    anchor_centering_status?: CenteringStatus | null
    anchor_centering_origin_wgs84?: [number, number, number] | null
    anchor_bbox_min_wgs84?: [number, number, number] | null
    centering_origin?: [number, number, number] | null
    wgs84_position?: [number, number, number] | null
    bounding_box_min?: [number, number, number] | null
}

export type AnchorableMapFeature = {
    type?: string | null
    position?: [number, number, number] | null
    model?: (AnchorableModel & { type?: string | null }) | null
}

// Scene-space (Three.js) position for the active anchor mode. The Potree
// octree is always centered, so we pick the group's local scene-space spot:
//   - centered → [0, 0, 0]
//   - default  → centering_origin (raw [x, y, z] from the source file's
//                coordinate frame — projected CRS, scanner-local, whatever it
//                was). Placing the centered Potree here restores the cloud's
//                pre-centering placement as if ORI had never centered it.
//   - crs      → [0, 0, 0] (CRS is a Map-scene concept; in scene-space the
//                Potree group lives near origin)
//   - custom   → custom_anchor[0..2] (user-set scene-space position)
// Returns null when the requested mode has no usable data and the caller
// should leave model.position alone.
export function resolveScenePosition(model: AnchorableModel): [number, number, number] | null {
    const mode: AnchorMode = model.anchor_mode ?? 'default'

    if (mode === 'centered' || mode === 'crs') {
        return [0, 0, 0]
    }

    if (mode === 'default' && model.centering_origin && model.centering_origin[0] != null) {
        const [x, y, z] = model.centering_origin
        return [x, y, z ?? 0]
    }

    if (mode === 'custom' && model.custom_anchor && model.custom_anchor[0] != null) {
        const [x, y, z] = model.custom_anchor
        return [x, y, z ?? 0]
    }

    return null
}

// WGS84 (lng, lat, alt) position for the active anchor mode in a Map scene.
// The rendered Potree octree is centered around ORI's centering origin, so the
// geographic placement must use that same full WGS84 triple. Mixing the
// centered lon/lat with bbox-min Z moves the already-centered octree vertically
// away from the baseline placement that pre-anchor Kartorium used.
// Custom uses custom_anchor[0..2] as [lng, lat, alt]. Legacy point clouds
// (processed before the anchors-block rollout) and non-point-cloud 3D models
// fall back to model.wgs84_position when it has usable coordinates.
//
// Returns null when no usable placement exists — caller decides whether to
// leave feature.position at its default (typically map center).
export function resolveMapPosition(model: AnchorableModel): [number, number, number] | null {
    const mode: AnchorMode = model.anchor_mode ?? 'default'

    if (mode === 'custom') {
        if (model.custom_anchor && model.custom_anchor[0] != null) {
            return [model.custom_anchor[0], model.custom_anchor[1], model.custom_anchor[2] ?? 0]
        }
        return null
    }

    if (model.anchor_centering_origin_wgs84 && model.anchor_centering_origin_wgs84[0] != null) {
        const [lng, lat, height] = model.anchor_centering_origin_wgs84
        return [lng, lat, height ?? 0]
    }

    if (model.anchor_bbox_min_wgs84 && model.anchor_bbox_min_wgs84[0] != null) {
        const [lng, lat, height] = model.anchor_bbox_min_wgs84
        return [lng, lat, height ?? 0]
    }

    // Legacy fallback: models without an anchors block, or non-point-cloud
    // 3D models, have a cached wgs84_position seeded at processing time.
    // Reject [0, 0, 0] sentinels — those mean "no georeference known."
    if (model.wgs84_position && (model.wgs84_position[0] !== 0 || model.wgs84_position[1] !== 0)) {
        const [lng, lat, height] = model.wgs84_position
        return [lng, lat, height ?? 0]
    }

    return null
}

function hasUsableMapPosition(position: [number, number, number] | null | undefined): position is [number, number, number] {
    return !!position && (position[0] !== 0 || position[1] !== 0)
}

// Effective WGS84 (lng, lat, alt) placement for a Feature in a Map scene.
// Point clouds with anchor metadata are anchored by model metadata; legacy
// point clouds and ordinary features use the feature's own position.
export function resolveMapFeaturePosition(feature: AnchorableMapFeature): [number, number, number] | null {
    const model = feature?.model
    const isAnchoredPointCloud = feature?.type === '3D'
        && model?.type === 'point-cloud'
        && model.anchor_mode !== 'custom'
        && hasAnchorMetadata(model)

    if (isAnchoredPointCloud) {
        return resolveMapPosition(model) ?? feature.position ?? null
    }

    if (feature?.type === '3D' && model?.type === 'point-cloud' && model.anchor_mode !== 'custom') {
        return hasUsableMapPosition(feature.position)
            ? feature.position
            : resolveMapPosition(model) ?? feature.position ?? null
    }

    return feature?.position ?? null
}

// True when the user can pick "Centered". Centering is a property of the
// Potree build — ORI reports it via anchor_centering_status. A 'skipped_overflow'
// file wasn't centered, so picking Centered has no meaning.
export function canSelectCentered(model: AnchorableModel): boolean {
    return model.anchor_centering_status === 'centered'
}

// True when the user can pick "Default". Requires the raw pre-centering
// (x, y, z) — ORI provides this via anchors.centering_origin.{x,y,z} for any
// file it successfully centered, independent of CRS recognition.
export function canSelectDefault(model: AnchorableModel): boolean {
    return !!model.centering_origin && model.centering_origin[0] != null
}

// True when the user can pick "CRS". Requires a WGS84-converted centering
// origin — ORI only emits this when the source file's CRS was recognized.
// Files with no embedded CRS (or one PDAL couldn't read) get null here.
export function canSelectCRS(model: AnchorableModel): boolean {
    return !!model.anchor_centering_origin_wgs84
        && model.anchor_centering_origin_wgs84[0] != null
}

// True when the editor should surface the anchor selector at all. Hide it
// for legacy point clouds processed before the anchors-block rollout — they
// have neither centering metadata nor a centering_origin to switch between.
export function hasAnchorMetadata(model: AnchorableModel): boolean {
    return model.anchor_centering_status != null
        || (!!model.centering_origin && model.centering_origin[0] != null)
}

// Centered backfills can lack the newer anchor columns while still carrying a
// centroid WGS84 anchor. Legacy bbox-min rows have wgs84_position.z equal to
// bounding_box_min.z; centered rows differ by the centering-origin offset.
export function hasCenteredBackfillAnchor(model: AnchorableModel): boolean {
    const wgsZ = model.wgs84_position?.[2]
    const bboxMinZ = model.bounding_box_min?.[2]

    if (wgsZ == null || bboxMinZ == null) return false

    return Math.abs(wgsZ - bboxMinZ) > 1e-3
}

// True when the cloud has been moved off its current anchor-mode position
// (i.e. typed-in Position inputs, Center buttons, or TransformControls drag
// landed somewhere other than what centered/default/crs would resolve to).
// Callers should flip anchor_mode to 'custom' on a true return and leave
// custom_anchor untouched so the Update Custom Anchor button surfaces via
// hasUncommittedCustomAnchor.
//
// `position` is the scene-space [x,y,z] the move would land at — pass the
// model's current scene-space position. 1mm tolerance guards against form
// roundtrip noise on inputs that round-tripped through string conversion.
export function hasMovedOffAnchorMode(
    model: AnchorableModel,
    position: [number, number, number] | null | undefined,
): boolean {
    if (!position) return false
    if (!hasAnchorMetadata(model)) return false
    const mode = model.anchor_mode
    if (mode !== 'centered' && mode !== 'default' && mode !== 'crs') return false

    const expected = resolveScenePosition(model)
    if (!expected) return false

    return Math.abs(position[0] - expected[0]) > 1e-3
        || Math.abs(position[1] - expected[1]) > 1e-3
        || Math.abs(position[2] - expected[2]) > 1e-3
}
